import React, { useEffect, useState, useMemo, useRef } from "react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import 'ag-grid-enterprise';
import ConfirmDeleteModal from "../confirmdeletemodal";
import loaderGif from "../../../assets/loader.gif";
import api from '../../../config/axiosConfig';


const ProgramUnits = () => {
    const [formData, setFormData] = useState({
        id: null,  // Track if it's a new entry or an edit
        programId: '',
        breakfast: '',
        lunch: '',
        dinner: '',
        wellnessTreatments: '',
        spaOrMassage: '',
        yoga: '',
        fitness: '',
        meditation: '',
        beachActivities: '',
        games: '',
        bonding: '',
        equipmentRental: '',
        conferenceRoomFor2Hr: '',
        offsiteActivity: '',
        excursion: '',
        airportTransfer: '',
        isactive: true,
        createddate: '',
        modifieddate: ''
    });

    const [rowData, setRowData] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false); // To track if we are in edit mode
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loader, setLoader] = useState(false);
    const [programs, setPrograms] = useState();

    const getData = async () => {
        try {
            const response = await api.get('/programunits');
            setRowData(response.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const getPrograms = async () => {
        try {
            const response = await api.get('/programs'); // Adjust the API endpoint as needed
            setPrograms(response.data); 
        } catch (error) {
            console.error("Error fetching programs:", error);
        }
    };

    useEffect(() => {
        getPrograms();
        getData();
    }, []);

    const gridRef = useRef();

    // Column definitions for Ag-Grid
    const columnDefs = [

        { headerName: 'ID', field: 'id', sort: "desc" , filter: 'agNumberColumnFilter'},
        { headerName: 'Program ID', field: 'programId', filter: 'agNumberColumnFilter' },
        { headerName: 'Breakfast', field: 'breakfast', filter: 'agTextColumnFilter' },
        { headerName: 'Lunch', field: 'lunch', filter: 'agTextColumnFilter' },
        { headerName: 'Dinner', field: 'dinner', filter: 'agTextColumnFilter' },
        { headerName: 'Wellness Treatments', field: 'wellnessTreatments', filter: 'agTextColumnFilter' },
        { headerName: 'Spa Or Massage', field: 'spaOrMassage', filter: 'agTextColumnFilter' },
        { headerName: 'Yoga', field: 'yoga', filter: 'agTextColumnFilter' },
        { headerName: 'Fitness', field: 'fitness', filter: 'agTextColumnFilter' },
        { headerName: 'Meditation', field: 'meditation', filter: 'agTextColumnFilter' },
        { headerName: 'Beach Activities', field: 'beachActivities', filter: 'agTextColumnFilter' },
        { headerName: 'Games', field: 'games', filter: 'agTextColumnFilter' },
        { headerName: 'Bonding', field: 'bonding', filter: 'agTextColumnFilter' },
        { headerName: 'Equipment Rental', field: 'equipmentRental', filter: 'agTextColumnFilter' },
        { headerName: 'Conference Room For 2 Hr', field: 'conferenceRoomFor2Hr', filter: 'agTextColumnFilter' },
        { headerName: 'Offsite Activity', field: 'offsiteActivity', filter: 'agTextColumnFilter' },
        { headerName: 'Excursion', field: 'excursion', filter: 'agTextColumnFilter' },
        { headerName: 'Airport Transfer', field: 'airportTransfer', filter: 'agTextColumnFilter' },
        { headerName: 'Is Active', field: 'isactive' },
        { headerName: 'Created By', field: 'createdby', filter: 'agTextColumnFilter' },
        { headerName: 'Created At', field: 'createddate', filter: 'agNumberColumnFilter' },
        { headerName: 'Updated By', field: 'modifiedby', filter: 'agTextColumnFilter' },
        { headerName: 'Updated At', field: 'modifieddate', filter: 'agNumberColumnFilter' },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: (params) => (
                <Button
                    variant="danger"
                    className="btn-sm"
                    onClick={() => handleDelete(params.data)}
                >
                    <i className="bi bi-trash3"></i>
                </Button>
            ),
        },
    ];

    const sideBar = useMemo(() => {
        return {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                },
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel',
                }
            ],
            defaultToolPanel: '',
        };
    }, []);

    const autoGroupColumnDef = {
        headerName: 'Group',
        width: 500,
        cellRenderer: 'agGroupCellRenderer',
    };

    // Server-Side Datasource
    const datasource = {
        getRows: async function (params) {
            try {
                const { request } = params;
                
                const response = await api.post('/programunits/ag',{ ...request } );

                const { rows, lastRow } = response.data;

                // Send data back to AG Grid
                params.successCallback(rows, lastRow);
            } catch (error) {
                console.error("Error fetching rows:", error);
                params.failCallback();
            }
        }
    };

    const gridOptions = {
        rowModelType: 'serverSide',
        columnDefs: columnDefs,
        rowHeight: 60,

        defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            floatingFilter: true,
            enableValue: true,
            enableRowGroup: true,
            enablePivot: true,
            filterParams: {
                buttons: ["reset"],
                closeOnApply: true,
            },
        },

        sideBar: sideBar,
        rowGroupPanelShow: 'always',
        rowSelection: 'single',
        columnHoverHighlight: true,
        suppressRowGroupHidesColumns: true,
        cacheBlockSize: 20
    }

    const onGridReady = (params) => {
        params.api.setServerSideDatasource(datasource);
    };

    // Row selection handler
    const onRowSelected = (event) => {
        if (event.node.isSelected()) {
            const selectedRow = event.data;
            setFormData({
                id: selectedRow.id,
                programId: selectedRow.programId,
                breakfast: selectedRow.breakfast,
                lunch: selectedRow.lunch,
                dinner: selectedRow.dinner,
                wellnessTreatments: selectedRow.wellnessTreatments,
                spaOrMassage: selectedRow.spaOrMassage,
                yoga: selectedRow.yoga,
                fitness: selectedRow.fitness,
                meditation: selectedRow.meditation,
                beachActivities: selectedRow.beachActivities,
                games: selectedRow.games,
                bonding: selectedRow.bonding,
                equipmentRental: selectedRow.equipmentRental,
                conferenceRoomFor2Hr: selectedRow.conferenceRoomFor2Hr,
                offsiteActivity: selectedRow.offsiteActivity,
                excursion: selectedRow.excursion,
                airportTransfer: selectedRow.airportTransfer,
                isactive: selectedRow.isactive
            });

            setIsEditMode(true);  // Switch to edit mode
            setSelectedRow(selectedRow)
        }
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle form submission
    // Handle form submission for adding/updating media
    const handleAddData = async (e) => {
        e.preventDefault();
        setLoader(true);
        // Prepare data for media entry
        const programsUnitsData = {
            isactive: formData.isactive,
            programId: formData.programId,
            breakfast: formData.breakfast,
            lunch: formData.lunch,
            dinner: formData.dinner,
            wellnessTreatments: formData.wellnessTreatments,
            spaOrMassage: formData.spaOrMassage,
            yoga: formData.yoga,
            fitness: formData.fitness,
            meditation: formData.meditation,
            beachActivities: formData.beachActivities,
            games: formData.games,
            bonding: formData.bonding,
            equipmentRental: formData.equipmentRental,
            conferenceRoomFor2Hr: formData.conferenceRoomFor2Hr,
            offsiteActivity: formData.offsiteActivity,
            excursion: formData.excursion,
            airportTransfer: formData.airportTransfer,
            createdat: new Date(),

        };
        // Determine whether to update or create a new entry
        const url = isEditMode ? `/programunits/${formData.id}` : '/programunits';
        const request = isEditMode ? api.put(url, programsUnitsData) : api.post(url, programsUnitsData);
        try {
            const response = await request;

            // Clear form fields and reset edit mode
            setFormData({ id: null, programId: '', breakfast: '', lunch: '', dinner: '', wellnessTreatments: '', spaOrMassage: '', yoga: '', fitness: '', meditation: '', beachActivities: '', games: '', bonding: '', equipmentRental: '', conferenceRoomFor2Hr: '', offsiteActivity: '', excursion: '', airportTransfer: '', isactive: true });
            setIsEditMode(false);
            setLoader(false);
            gridRef.current.api.refreshServerSideStore({ purge: true });
            // Refresh rowData to reflect updates
            getData();

        } catch (error) {
            console.error("Error adding/updating program unit:", error);
            setLoader(false);
        }
    };



    // Reset form for new entry
    const handleNewEntry = () => {
        setFormData({ id: null, programId: '', breakfast: '', lunch: '', dinner: '', wellnessTreatments: '', spaOrMassage: '', yoga: '', fitness: '', meditation: '', beachActivities: '', games: '', bonding: '', equipmentRental: '', conferenceRoomFor2Hr: '', offsiteActivity: '', excursion: '', airportTransfer: '', isactive: true });
        setIsEditMode(false);  // Switch to add mode
    };

    const handleDelete = () => {
        setShowModal(true);
    };

    const confirmDelete = async () => {
        try {
            await api.delete(`/programunits/${selectedRow.id}`);
            setShowModal(false);
            gridRef.current.api.refreshServerSideStore({ purge: true });
            getData();  // Refresh data after deletion
        } catch (error) {
            console.error('Error deleting program unit:', error);
        }
    };


    return (
        <>
            <Container fluid>
                <Row className="mt-4">
                    <h6 className="text-start">Program Units List</h6>
                    <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            gridOptions={gridOptions}
                            onGridReady={onGridReady}
                            onRowSelected={onRowSelected}
                        />
                    </div>
                </Row>
                <Row className="mt-4 border shadow p-4">
                    {loader && (
                        <div className="loader-overlay">
                            <img src={loaderGif} alt="Loading..." className="loader" />
                        </div>
                    )}
                    <Col md={12}>
                        <h6 className="text-start">{isEditMode ? "Edit Program Unit" : "Add Program Unit"}</h6>
                        <Form onSubmit={handleAddData}>
                            <Row>
                            <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Program</Form.Label>
                                        <InputGroup>
                                            <Form.Select
                                                name="programid"
                                                value={formData.programId}  // This will hold the selected program name
                                                onChange={(e) => {
                                                    const selectedProgramId = e.target.value;
                                                    setFormData(prevState => ({
                                                        ...prevState,
                                                        programId: selectedProgramId  
                                                    })); 
                                                }} required
                                            >
                                                <option value="">Select Program</option>
                                                {programs && programs.map((program) => (
                                                    <option key={program.id} value={program.id}>
                                                        {program.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Check
                                            type="checkbox"
                                            name="isactive"
                                            label="Is Active"
                                            checked={formData.isactive}
                                            onChange={handleChange}
                                            className="ms-4"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row><br />
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Breakfast</Form.Label>
                                        <Form.Select
                                            name="breakfast"
                                            value={formData.breakfast}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Breakfast Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Lunch</Form.Label>
                                        <Form.Select
                                            name="lunch"
                                            value={formData.lunch}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Lunch Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Dinner</Form.Label>
                                        <Form.Select
                                            name="dinner"
                                            value={formData.dinner}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Dinner Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Wellness Treatments</Form.Label>
                                        <Form.Select
                                            name="wellnessTreatments"
                                            value={formData.wellnessTreatments}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Wellness Treatments Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Spa Or Massage</Form.Label>
                                        <Form.Select
                                            name="spaOrMassage"
                                            value={formData.spaOrMassage}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Spa Or Massage Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Yoga</Form.Label>
                                        <Form.Select
                                            name="yoga"
                                            value={formData.yoga}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Yoga Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Fitness</Form.Label>
                                        <Form.Select
                                            name="fitness"
                                            value={formData.fitness}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Fitness Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Meditation</Form.Label>
                                        <Form.Select
                                            name="meditation"
                                            value={formData.meditation}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Meditation Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Beach Activities</Form.Label>
                                        <Form.Select
                                            name="beachActivities"
                                            value={formData.beachActivities}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Beach Activities Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Games</Form.Label>
                                        <Form.Select
                                            name="games"
                                            value={formData.games}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Games Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Bonding</Form.Label>
                                        <Form.Select
                                            name="bonding"
                                            value={formData.bonding}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Bonding Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Equipment Rental</Form.Label>
                                        <Form.Select
                                            name="equipmentRental"
                                            value={formData.equipmentRental}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Equipment Rental Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Conference Room For 2Hrs</Form.Label>
                                        <Form.Select
                                            name="conferenceRoomFor2Hr"
                                            value={formData.conferenceRoomFor2Hr}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Conference Room For 2Hrs Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Offsite Activity</Form.Label>
                                        <Form.Select
                                            name="offsiteActivity"
                                            value={formData.offsiteActivity}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Offsite Activity Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Excursion</Form.Label>
                                        <Form.Select
                                            name="excursion"
                                            value={formData.excursion}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Excursion Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Airport Transfer</Form.Label>
                                        <Form.Select
                                            name="airportTransfer"
                                            value={formData.airportTransfer}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Airport Transfer Option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="NA">NA</option>
                                            <option value="On Order">On Order</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Button variant="primary" type="submit">
                                {isEditMode ? "Update" : "Add"}
                            </Button>
                            {isEditMode ?
                                <Button variant="secondary" className="ms-3" onClick={handleNewEntry}>
                                    Create New Entry
                                </Button>
                                : ''
                            }
                        </Form>
                    </Col>
                </Row>
            </Container>

            {/* Modal for confirming deletion */}
            <ConfirmDeleteModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={confirmDelete}
                title="Confirm Deletion"
                body={`Are you sure you want to delete ${selectedRow?.name}?`}
                confirmText="Delete"
            />




        </>
    );
};

export default ProgramUnits;